import React, { useEffect, useState } from "react";
import { fetchUserAttributes } from "aws-amplify/auth";
import { toast } from "react-toastify";
import {
  PlatformClinic,
  PlatformClinicsAPI,
  hasActiveSubscription,
  isActiveManualTrial,
} from "../../services/platformClinics.service";
import { PlatformKycAPI } from "../../services/platformKyc.service";
import { BillingAPI } from "../../services/billing.service";
import PlatformAdminNav from "../../components/platform/PlatformAdminNav";
import PlatformAdminHeader from "../../components/platform/PlatformAdminHeader";
import FixUserRowModal from "../../components/platform/FixUserRowModal";
import GrantCreditsModal from "../../components/platform/GrantCreditsModal";
import ClinicDocumentsModal from "../../components/platform/ClinicDocumentsModal";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Per-clinic KYC roll-up. The cross-clinic KYC feed (/platform/kyc/pending) only
 * exposes docs awaiting review, so we can reliably surface "Pending" (has docs in
 * the review queue) vs "—" (nothing pending) — not Verified/Rejected, which are
 * not exposed cross-clinic.
 */
const KycCell: React.FC<{ pending: boolean }> = ({ pending }) =>
  pending ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
      <span className="text-orange-600">Pending</span>
    </span>
  ) : (
    <span className="text-slate-300">—</span>
  );

const OnboardedClinicsPage: React.FC = () => {
  const [clinics, setClinics] = useState<PlatformClinic[]>([]);
  const [pendingKyc, setPendingKyc] = useState<Set<string>>(new Set());
  const [planNames, setPlanNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState<string>("");

  // Per-row in-flight guard for Grant/Extend Trial.
  const [busyId, setBusyId] = useState<string | null>(null);
  // Per-row in-flight guard for feature toggle.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Clinic whose Fix User Row modal is open.
  const [fixClinic, setFixClinic] = useState<PlatformClinic | null>(null);
  // Clinic whose Add Credits modal is open.
  const [creditsClinic, setCreditsClinic] = useState<PlatformClinic | null>(null);
  // Clinic whose Documents modal is open.
  const [docsClinic, setDocsClinic] = useState<PlatformClinic | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([PlatformClinicsAPI.listClinics(), fetchUserAttributes()])
      .then(([rows, attrs]) => {
        setClinics(rows);
        setAdminEmail(attrs.email ?? "");
      })
      .catch(() => setError("Failed to load onboarded clinics. Please refresh."))
      .finally(() => setLoading(false));

    // Best-effort side feeds — a failure here must not blank the table.
    PlatformKycAPI.listQueue()
      .then(items => setPendingKyc(new Set(items.filter(i => i.status === "submitted").map(i => i.clinic_id))))
      .catch(() => { /* leave KYC column as "—" */ });

    BillingAPI.getPlans()
      .then(plans => setPlanNames(Object.fromEntries(plans.map(p => [p.plan_id, p.name]))))
      .catch(() => { /* fall back to generic plan labels */ });
  };

  useEffect(load, []);

  const handleGrant = async (clinic: PlatformClinic, force: boolean) => {
    setBusyId(clinic.clinic_id);
    try {
      await PlatformClinicsAPI.grantTrial(clinic.clinic_id, { days: 14, tier: "base", force });
      toast.success(
        force
          ? `Trial extended for ${clinic.name}`
          : `14-day trial granted to ${clinic.name}`
      );
      load();
    } catch (e: any) {
      toast.error(e?.message || (force ? "Failed to extend trial" : "Failed to grant trial"));
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleAiScribe = async (clinic: PlatformClinic) => {
    const flag = clinic.features.find(f => f.feature === "ai_scribe");
    const currentlyEnabled = flag?.is_enabled ?? false;
    const newEnabled = !currentlyEnabled;
    setTogglingId(clinic.clinic_id);
    try {
      await PlatformClinicsAPI.toggleFeature(clinic.clinic_id, "ai_scribe", newEnabled);
      // Update local state instead of refetching all clinics
      setClinics(prev => prev.map(c => {
        if (c.clinic_id !== clinic.clinic_id) return c;
        const updatedFeatures = c.features.some(f => f.feature === "ai_scribe")
          ? c.features.map(f => f.feature === "ai_scribe" ? { ...f, is_enabled: newEnabled } : f)
          : [...c.features, { feature: "ai_scribe", is_enabled: newEnabled, is_trial: false, expires_at: null }];
        return { ...c, features: updatedFeatures };
      }));
      toast.success(`AI Scribe ${newEnabled ? "enabled" : "disabled"} for ${clinic.name}`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to toggle AI Scribe");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <PlatformAdminNav adminEmail={adminEmail} />

      <div className="max-w-7xl mx-auto px-8 py-8">
        <PlatformAdminHeader subtitle="PlatformAdmin only — manage clinic subscriptions and onboarding." />

        {loading && (
          <div className="text-sm text-slate-500 py-12 text-center">Loading clinics…</div>
        )}

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && clinics.length === 0 && (
          <div className="flex justify-center pt-16">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-10 py-10 text-center space-y-2 max-w-sm w-full">
              <p className="font-semibold text-slate-800">No onboarded clinics yet</p>
              <p className="text-sm text-slate-500">
                Clinics appear here once they complete onboarding.
              </p>
            </div>
          </div>
        )}

        {!loading && !error && clinics.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-medium text-slate-500">
                  <th className="px-5 py-3">Clinic</th>
                  <th className="px-5 py-3">Admin</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Expires</th>
                  <th className="px-5 py-3">Doctors</th>
                  <th className="px-5 py-3">KYC</th>
                  <th className="px-5 py-3">AI Scribe</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                  <th className="px-5 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {clinics.map(c => {
                  const activeSub  = hasActiveSubscription(c);
                  const manualTrial = isActiveManualTrial(c);
                  const showGrant  = !activeSub;
                  const showExtend = manualTrial;
                  const showFix    = c.doctor_count === 0 || c.admin_user_id == null;
                  // No "no actions" case any more: Add Credits always renders.
                  const rowBusy    = busyId === c.clinic_id;

                  const planName = c.subscription
                    ? planNames[c.subscription.plan_id] ??
                      (c.subscription.is_trial_row ? "Trial" : "Active")
                    : null;
                  const showTrialPill = !!c.subscription?.is_trial_row && !!planNames[c.subscription.plan_id];

                  return (
                    <tr
                      key={c.clinic_id}
                      onClick={() => setDocsClinic(c)}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      {/* Clinic */}
                      <td className="px-5 py-4">
                        <p className="font-semibold text-slate-900">{c.name}</p>
                        {c.location && (
                          <p className="text-xs text-slate-400 mt-0.5">{c.location}</p>
                        )}
                      </td>

                      {/* Admin */}
                      <td className="px-5 py-4 text-slate-700">
                        {c.admin_name ?? c.admin_email ?? <span className="text-slate-400">—</span>}
                      </td>

                      {/* Plan */}
                      <td className="px-5 py-4">
                        {planName ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{planName}</span>
                            {showTrialPill && (
                              <span className="rounded bg-orange-100 text-orange-700 text-[11px] font-semibold px-1.5 py-0.5">
                                Trial
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      {/* Expires */}
                      <td className="px-5 py-4 text-slate-700">
                        {c.subscription?.current_period_end
                          ? formatDate(c.subscription.current_period_end)
                          : <span className="text-slate-400">—</span>}
                      </td>

                      {/* Doctors */}
                      <td className={`px-5 py-4 font-semibold ${c.doctor_count === 0 ? "text-red-600" : "text-slate-900"}`}>
                        {c.doctor_count}
                      </td>

                      {/* KYC */}
                      <td className="px-5 py-4">
                        <KycCell pending={pendingKyc.has(c.clinic_id)} />
                      </td>

                      {/* AI Scribe Toggle */}
                      <td className="px-5 py-4">
                        {(() => {
                          const flag = c.features.find(f => f.feature === "ai_scribe");
                          const enabled = flag?.is_enabled ?? false;
                          const busy = togglingId === c.clinic_id;
                          return (
                            <button
                              onClick={() => handleToggleAiScribe(c)}
                              disabled={busy}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                busy ? "opacity-60 cursor-wait" : ""
                              } ${enabled ? "bg-teal-500" : "bg-slate-300"}`}
                            >
                              {busy ? (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                </span>
                              ) : (
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    enabled ? "translate-x-6" : "translate-x-1"
                                  }`}
                                />
                              )}
                            </button>
                          );
                        })()}
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {showExtend && (
                            <button
                              onClick={() => handleGrant(c, true)}
                              disabled={rowBusy}
                              className="border border-[#2563eb] text-[#2563eb] text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                              {rowBusy ? "Extending…" : "Extend Trial"}
                            </button>
                          )}
                          {showGrant && (
                            <button
                              onClick={() => handleGrant(c, false)}
                              disabled={rowBusy}
                              className="bg-[#2563eb] text-white text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                              {rowBusy ? "Granting…" : "Grant Trial"}
                            </button>
                          )}
                          {showFix && (
                            <button
                              onClick={() => setFixClinic(c)}
                              disabled={rowBusy}
                              className="border border-amber-400 text-amber-600 text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-amber-50 disabled:opacity-50 transition-colors whitespace-nowrap"
                            >
                              Fix User Row
                            </button>
                          )}
                          {/* Always offered: a clinic can exhaust its wallet on any
                              plan, and the row click opens the documents dialog, so
                              this must not bubble. */}
                          <button
                            onClick={e => { e.stopPropagation(); setCreditsClinic(c); }}
                            disabled={rowBusy}
                            className="border border-teal-400 text-teal-700 text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-teal-50 disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            Add Credits
                          </button>
                        </div>
                      </td>

                      {/* Chevron — signals the row opens the documents dialog */}
                      <td className="px-5 py-4 text-slate-300">›</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <FixUserRowModal
        isOpen={fixClinic !== null}
        clinic={fixClinic}
        onClose={() => setFixClinic(null)}
        onSuccess={load}
      />

      <GrantCreditsModal
        isOpen={creditsClinic !== null}
        clinic={creditsClinic}
        onClose={() => setCreditsClinic(null)}
        onSuccess={load}
      />

      <ClinicDocumentsModal
        isOpen={docsClinic !== null}
        clinic={docsClinic}
        onClose={() => setDocsClinic(null)}
      />
    </div>
  );
};

export default OnboardedClinicsPage;
